import {
  Channel,
  ChannelKind,
  Note,
  Pattern,
  Project,
  SynthParams,
  defaultSynthParams,
  uid,
} from './types';

let noteSeq = 0;
function note(start: number, len: number, midi: number, velocity: number): Note {
  return { id: `n${++noteSeq}`, start, length: len, note: midi, velocity };
}

function pattern(id: string, name: string, length: number, notes: Note[]): Pattern {
  return { id, name, length, notes };
}

function synth(partial: Partial<SynthParams>): SynthParams {
  return { ...defaultSynthParams(), ...partial };
}

function channel(
  name: string,
  kind: ChannelKind,
  color: string,
  patternId: string,
  partial: Partial<Channel> = {},
): Channel {
  return {
    id: uid(),
    name,
    kind,
    color,
    synth: defaultSynthParams(),
    drum: { kit: 'classic' },
    patternId,
    muted: false,
    solo: false,
    volume: 0.9,
    pan: 0,
    effects: [],
    sends: {},
    sampler: { sampleId: null, rootNote: 60, loop: false },
    ...partial,
  };
}

/**
 * A small but musical demo project so the app makes sound on first Play:
 * drums + bass + lead arpeggio, all in a 16-step loop.
 */
export function createDefaultProject(): Project {
  const drumsId = uid();
  const bassId = uid();
  const leadId = uid();
  const keysId = uid();

  const drums = pattern(drumsId, 'Drums', 16, [
    note(0, 1, 36, 110), note(8, 1, 36, 110),
    note(4, 1, 38, 100), note(12, 1, 38, 100),
    note(0, 1, 42, 70), note(1, 1, 42, 55), note(2, 1, 42, 70), note(3, 1, 42, 55),
    note(4, 1, 42, 70), note(5, 1, 42, 55), note(6, 1, 42, 70), note(7, 1, 42, 85),
    note(8, 1, 42, 70), note(9, 1, 42, 55), note(10, 1, 42, 70), note(11, 1, 42, 55),
    note(12, 1, 42, 70), note(13, 1, 42, 55), note(14, 1, 42, 70), note(15, 1, 42, 85),
    note(2, 1, 46, 50), note(6, 1, 46, 50), note(10, 1, 46, 50), note(14, 1, 46, 50),
  ]);

  const bass = pattern(bassId, 'Bass', 16, [
    note(0, 2, 36, 100), note(3, 1, 36, 80), note(6, 2, 36, 100),
    note(8, 2, 36, 100), note(11, 1, 36, 80), note(14, 2, 39, 90),
  ]);

  const lead = pattern(leadId, 'Lead', 16, [
    note(0, 2, 60, 90), note(2, 2, 64, 80), note(4, 2, 67, 90), note(6, 2, 64, 80),
    note(8, 2, 69, 90), note(10, 2, 67, 80), note(12, 2, 64, 90), note(14, 2, 62, 80),
  ]);

  // Sampler "keys" over the built-in pluck kit (root C4).
  const keys = pattern(keysId, 'Keys', 16, [
    note(0, 4, 60, 90), note(4, 4, 64, 80), note(8, 4, 67, 90), note(12, 4, 69, 80),
  ]);

  const leadSynth = synth({
    osc1Wave: 'sawtooth',
    osc2Wave: 'square',
    osc2Level: 0.25,
    filterCutoff: 5200,
    filterResonance: 6,
    filterEnvAmount: 0.45,
    decay: 0.12,
    sustain: 0.4,
    release: 0.18,
  });

  const bassSynth = synth({
    osc1Wave: 'square',
    osc1Octave: -1,
    osc2Wave: 'sawtooth',
    osc2Octave: -1,
    osc2Level: 0.5,
    fmDepth: 0.12,
    filterCutoff: 900,
    filterResonance: 8,
    filterEnvAmount: 0.5,
    attack: 0.003,
    decay: 0.1,
    sustain: 0.7,
    release: 0.12,
  });

  const drumsCh = channel('Drums', 'drum', '#4cc2ff', drumsId, { volume: 1.0 });
  const bassCh = channel('Bass', 'synth', '#3ddc84', bassId, { synth: bassSynth, volume: 0.8 });
  const leadCh = channel('Lead', 'synth', '#b06bff', leadId, { synth: leadSynth, volume: 0.75 });
  const keysCh = channel('Keys', 'sampler', '#ffb454', keysId, {
    volume: 0.7,
    sampler: { sampleId: 'builtin-pluck', rootNote: 60, loop: false },
  });

  // Demo session: a full "Groove" row, a drums+bass "Break", a "Build" row.
  const scenes = [
    {
      id: uid(),
      name: 'Groove',
      cells: {
        [drumsCh.id]: drumsId,
        [bassCh.id]: bassId,
        [leadCh.id]: leadId,
        [keysCh.id]: keysId,
      },
    },
    {
      id: uid(),
      name: 'Break',
      cells: {
        [drumsCh.id]: drumsId,
        [bassCh.id]: bassId,
        [leadCh.id]: null,
        [keysCh.id]: null,
      },
    },
    {
      id: uid(),
      name: 'Build',
      cells: {
        [drumsCh.id]: null,
        [bassCh.id]: null,
        [leadCh.id]: leadId,
        [keysCh.id]: keysId,
      },
    },
  ];

  return {
    name: 'Untitled',
    bpm: 120,
    timeSig: { beats: 4, unit: 4 },
    countInBars: 0,
    loop: { enabled: true, startStep: 0, endStep: 16 },
    patterns: { [drumsId]: drums, [bassId]: bass, [leadId]: lead, [keysId]: keys },
    channels: [drumsCh, bassCh, leadCh, keysCh],
    buses: [],
    playlist: [],
    scenes,
    masterVolume: 0.9,
  };
}